import { GetNotificationsInput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/GetNotificationsInput'
import { GetNotificationsOutput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/GetNotificationsOutput'
// Side-effect import: registers all concrete types in modelRegistrySingleton
// so Coral can deserialize polymorphic fields (NotificationMetadata, AggregatedMetadata, etc.)
import '@amzn/coral_com-amazon-jwostoremanagementnotification-coral'
import { GetFixtureInfoInput } from '@amzn/coral_com-amazon-ihmvirgo-coral/GetFixtureInfoInput'
import { ImageResource } from '@amzn/coral_com-amazon-ihmvirgo-coral/ImageResource'
import { FixtureTaskMetadata } from '@amzn/coral_com-amazon-jwostoremanagementnotification-coral/FixtureTaskMetadata'
import { getServiceClient } from '@api/client-factory'
import {
    INotificationData,
    IStoreNotification,
    IToolNotification,
} from '../types'
import {
    getToolName,
    transformAggregatedNotification,
    transformIndividualNotification,
} from '@services/notifications/transformers'

async function fetchFixtureImageData (storeId: string, notifications: GetNotificationsOutput['notifications']): Promise<Map<string, ImageResource>> {
    const fixtureIds = notifications
        .map((n) => (n.metadata as FixtureTaskMetadata)?.fixtureId)
        .filter(Boolean)

    if (fixtureIds.length === 0) return new Map()

    try {
        const input = GetFixtureInfoInput.clone({ storeId, fixtureIds })
        const response = await getServiceClient().getFixtureInfo(input).toPromise()
        const map = new Map<string, ImageResource>()
        response.fixtureInfoList?.forEach((info) => {
            if (info.fixtureId && info.imageResource) {
                map.set(info.fixtureId, info.imageResource)
            }
        })
        return map
    } catch (error) {
        console.error(`Failed to fetch fixture images for store ${storeId}:`, error)
        return new Map()
    }
}

async function transformStoreResponse (
    response: GetNotificationsOutput,
    isCurrentStore: boolean,
): Promise<IStoreNotification | null> {
    if (response.summary.totalNotifications === 0) {
        return null
    }

    const byType = new Map<string, GetNotificationsOutput['notifications']>()
    for (const notif of response.notifications) {
        const existing = byType.get(notif.notificationType) || []
        existing.push(notif)
        byType.set(notif.notificationType, existing)
    }

    const toolNotifications: IToolNotification[] = await Promise.all(
        response.summary.aggregatedNotifications.map(async (apiAgg) => {
            const individualsForType = byType.get(apiAgg.notificationType) || []
            const imageDataMap = await fetchFixtureImageData(response.summary.storeId, individualsForType)
            return {
                toolName: getToolName(apiAgg.notificationType),
                aggregatedNotification: transformAggregatedNotification(apiAgg),
                allIndividualNotifications: individualsForType.map(
                    (n) => transformIndividualNotification(n, isCurrentStore, imageDataMap),
                ),
            }
        }),
    )

    return {
        storeId: response.summary.storeId,
        toolNotifications,
    }
}

async function fetchStoreNotifications (storeId: string): Promise<GetNotificationsOutput> {
    const input = GetNotificationsInput.clone({ storeId })
    return getServiceClient().getNotifications(input).toPromise()
}

export class NotificationAPIService {
    public static async getNotifications (currentStoreId: string, otherStoreIds: string[] = []): Promise<INotificationData> {
        const allStoreIds = [currentStoreId, ...otherStoreIds.filter((id) => id !== currentStoreId)]

        const results = await Promise.allSettled(
            allStoreIds.map((storeId) => fetchStoreNotifications(storeId)),
        )

        const notifications: IStoreNotification[] = []
        const transformPromises: Array<Promise<void>> = []
        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                const isCurrentStore = allStoreIds[index] === currentStoreId
                transformPromises.push(
                    transformStoreResponse(result.value, isCurrentStore).then((storeNotif) => {
                        if (storeNotif) notifications.push(storeNotif)
                    }),
                )
            } else {
                console.error(`Failed to fetch notifications for store ${allStoreIds[index]}:`, result.reason?.message)
            }
        })

        await Promise.all(transformPromises)

        return { notifications }
    }
}
